import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {useDispatch, useSelector} from 'react-redux';

import IconSearch from '@spothero/icons/search';
import {AutoSuggestInput} from '@spothero/ui/backlog';

import {getAirports} from 'store/search/search-actions';

const STRIP_NON_ALPHANUMERIC = string => {
    const nonAlphaNumeric = /[^\w\d\s:]/gi;

    return string.replace(nonAlphaNumeric, '').toLowerCase();
};

const getAirportData = ({
    search: {
        data: {airports},
    },
}) => airports || [];

/**
 * ### `filterAirportData`
 * A function that takes in the raw airport data Array, and the text from the search
 * input field, and returns a filtered list of airport data that matches the searchText.
 *
 * @function filterAirportData
 * @param {{object}} filterArguments
 * - The data and search query to pass into the filter.
 * @param {Array} filterArguments.returnedAirportData
 * - The Array of raw airport data retrived from state.
 * @param {string} filterArguments.searchText
 * - The value of the search input field.
 * @returns {Array.<{label: string, value: string, id: (string|number)}>}
 * The filtered Array of airport data that matches the searchText provided.
 */
const filterAirportData = ({returnedAirportData, searchText}) =>
    returnedAirportData.reduce(
        (acc, {name: label, iata_code: iataCode, city}, index) => {
            if (
                STRIP_NON_ALPHANUMERIC(
                    [label, iataCode, city].join(' ')
                ).includes(STRIP_NON_ALPHANUMERIC(searchText))
            ) {
                acc.push({
                    label: `${label} (${iataCode})`,
                    value: iataCode,
                    id: iataCode || index,
                });
            }

            return acc;
        },
        []
    );

const SelectAirport = ({
    className,
    onSelect,
    onClear,
    searchText = '',
    showSuggestions,
    ...props
}) => {
    const dispatch = useDispatch();
    const classes = classnames('SelectAirport', className);

    useEffect(() => {
        dispatch(getAirports());
    }, [dispatch]);
    const returnedAirportData = useSelector(getAirportData);
    const filteredAirportData = filterAirportData({
        returnedAirportData,
        searchText,
    });
    const suggestionSelectBehavior = filteredAirportData.length
        ? onSelect
        : onClear;
    const airportSuggestions = showSuggestions
        ? searchText && !filteredAirportData.length
            ? [
                  {
                      label: 'No Airports Found',
                      value: '',
                      id: 'null',
                  },
              ]
            : filteredAirportData
        : [];

    return (
        <AutoSuggestInput
            {...props}
            type="search"
            autoActivateFirstSuggest={Boolean(filteredAirportData.length)}
            className={classes}
            defaultValue={searchText}
            icon={<IconSearch />}
            onClear={onClear}
            onSuggestionSelect={suggestionSelectBehavior}
            suggestions={airportSuggestions}
        />
    );
};

SelectAirport.propTypes = {
    className: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    searchText: PropTypes.string,
    showSuggestions: PropTypes.bool.isRequired,
};

export default SelectAirport;
